import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Switch } from "@mui/material";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import Button1 from "../../form/Button1";
import IconComponent from "../../HOC/Icon";
import { getAdminCustomKey } from "../../../controllers/settings/supplier/getAdminCustomKey";
import { changeAdminCustomKeyStatus } from "../../../controllers/settings/supplier/changeAdminCustomKeyStatus";
import {
  updateAdminAmadeusKey,
  updateAdminAmadeusSoapKey,
  updateAdminSabreKey,
  updateAdminTravelportKey,
} from "../../../controllers/settings/supplier/updateAdminSupplierKey";
import {
  OfficeIdSupplierFormPanel,
  toApiPayload,
} from "./CustomerOfficeIdSupplierForms";

const style = {
  position: "absolute",
  top: "50%",
  right: "0%",
  transform: "translate(0%, -50%)",
  width: window.innerWidth > 600 ? "80vw" : "100vw",
  maxWidth: window.innerWidth > 600 ? "560px" : "100vw",
  bgcolor: "background.paper",
  boxShadow: 24,
};

const OFFICE_SUPPLIERS = [
  {
    id: "amadeus",
    authKey: "amadeusAuth",
    name: "Amadeus",
    badgeClass: "bg-[#E8F0FE] text-[#1A73E8]",
    badge: "a",
  },
  {
    id: "travelport",
    authKey: "travelportAuth",
    name: "Travel Port",
    badgeClass: "bg-gray-100 text-gray-900",
    badge: "T",
  },
  {
    id: "sabre",
    authKey: "sabreAuth",
    name: "Sabre",
    badgeClass: "bg-[#FDE8E8] text-[#E31837]",
    badge: "S",
  },
  {
    id: "amadeusSoap",
    authKey: "amadeusSoapAuth",
    name: "Amadeus Soap",
    badgeClass: "bg-[#E8F0FE] text-[#1A73E8]",
    badge: "a",
  },
];

const EDITABLE_SUPPLIER_IDS = new Set([
  "amadeus",
  "travelport",
  "sabre",
  "amadeusSoap",
]);

const UPDATE_ADMIN_SUPPLIER_KEY = {
  amadeus: updateAdminAmadeusKey,
  travelport: updateAdminTravelportKey,
  sabre: updateAdminSabreKey,
  amadeusSoap: updateAdminAmadeusSoapKey,
};

function mapOfficeKeyResponse(apiData) {
  const suppliers = {};
  for (const supplier of OFFICE_SUPPLIERS) {
    suppliers[supplier.id] = apiData?.[supplier.authKey] || { isActive: false };
  }
  return {
    ownKey: Boolean(apiData?.ownKey),
    suppliers,
  };
}

function OfficeIdSupplierListShimmer() {
  return (
    <div className="rounded-2xl border border-[#E7EDF5] bg-white divide-y animate-pulse">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div key={idx} className="flex items-center gap-4 px-4 py-4">
          <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
          <div className="h-4 flex-1 max-w-[140px] rounded bg-gray-200" />
          <div className="h-6 w-11 shrink-0 rounded-full bg-gray-200" />
          <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

export default function CustomerOfficeIdModal({ open, setOpen, data }) {
  const [loading, setLoading] = useState(false);
  const [officeKeyData, setOfficeKeyData] = useState({
    ownKey: false,
    suppliers: {},
  });
  const [togglingId, setTogglingId] = useState(null);
  const [editSupplier, setEditSupplier] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [amadeusSoapFiles, setAmadeusSoapFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const accountId = data?._id;
  const { suppliers } = officeKeyData;

  const loadSuppliers = async () => {
    if (!accountId) return;
    setLoading(true);
    const res = await getAdminCustomKey(accountId);
    setLoading(false);
    if (res.return) {
      setOfficeKeyData(mapOfficeKeyResponse(res.data));
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  };

  useEffect(() => {
    if (open && accountId) {
      loadSuppliers();
    }
    if (!open) {
      setEditSupplier(null);
      setEditForm({});
      setAmadeusSoapFiles([]);
    }
    //eslint-disable-next-line
  }, [open, accountId]);

  const handleClose = () => setOpen(false);

  const handleBackToList = () => {
    setEditSupplier(null);
    setEditForm({});
    setAmadeusSoapFiles([]);
  };

  const handleToggle = async (supplier, checked) => {
    if (!accountId) return;
    setTogglingId(supplier.id);
    const res = await changeAdminCustomKeyStatus(accountId, {
      [supplier.id]: checked,
    });
    setTogglingId(null);
    if (res.return) {
      enqueueSnackbar("Supplier status updated", { variant: "success" });
      await loadSuppliers();
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  };

  const handleSaveEdit = async () => {
    const updateKey = UPDATE_ADMIN_SUPPLIER_KEY[editSupplier?.id];
    if (!accountId || !updateKey) return;

    setSaving(true);
    const payload = toApiPayload(editSupplier.id, editForm);
    if (editSupplier?.id === "amadeusSoap") {
      payload._files = amadeusSoapFiles;

      if (!amadeusSoapFiles.length) {
        setSaving(false);
        enqueueSnackbar("Please upload a ZIP file containing the WSDL", {
          variant: "error",
        });
        return;
      }

      const requiredFields = [
        ["username", "Username"],
        ["password", "Password"],
        ["officeId", "Office ID"],
        ["dutyCode", "Duty Code"],
        ["wsap", "WSAP"],
        ["wsdlFileName", "WSDL entry filename"],
      ];
      const missing = requiredFields
        .filter(([key]) => !String(payload[key] ?? "").trim())
        .map(([, label]) => label);

      if (missing.length) {
        setSaving(false);
        enqueueSnackbar(`Please fill in: ${missing.join(", ")}`, {
          variant: "error",
        });
        return;
      }
    }
    const res = await updateKey(accountId, payload);
    setSaving(false);
    if (res.return) {
      enqueueSnackbar("Supplier credentials updated", { variant: "success" });
      handleBackToList();
      await loadSuppliers();
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  };

  const headerTitle = useMemo(() => {
    if (editSupplier) return `Edit ${editSupplier.name}`;
    return data?.detail?.agencyName || "Office ID";
  }, [data?.detail?.agencyName, editSupplier]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="customer-office-id-modal"
    >
      <Box sx={style}>
        <div className="flex flex-col font-main h-screen max-w-[100vw]">
          <div className="w-full max-w-[100vw] bg-primary1 p-4 text-white font-semibold flex justify-between items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {editSupplier ? (
                <button
                  type="button"
                  onClick={handleBackToList}
                  className="shrink-0 flex items-center justify-center"
                  aria-label="Back to suppliers"
                >
                  <Icon
                    icon="material-symbols:arrow-back"
                    className="text-secondary text-2xl"
                  />
                </button>
              ) : null}
              <span className="truncate">{headerTitle}</span>
            </div>
            <button type="button" onClick={handleClose} className="shrink-0">
              <Icon
                icon="material-symbols:close"
                className="text-secondary text-3xl"
              />
            </button>
          </div>

          <div className="flex flex-1 flex-col overflow-y-auto scroll-hide p-4 gap-3">
            {editSupplier ? (
              <div className="flex flex-col gap-6 flex-1">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${editSupplier.badgeClass}`}
                  >
                    {editSupplier.badge}
                  </span>
                  <span className="text-sm font-medium text-primary">
                    {editSupplier.name}
                  </span>
                </div>

                <OfficeIdSupplierFormPanel
                  supplierId={editSupplier.id}
                  value={suppliers?.[editSupplier.id]}
                  onChange={setEditForm}
                  onWsdlFilesChange={setAmadeusSoapFiles}
                />

                <div className="flex gap-3 mt-auto pt-4">
                  <Button1
                    type="button"
                    className="btn-theme-light flex-1"
                    onClick={handleBackToList}
                  >
                    Cancel
                  </Button1>
                  <Button1
                    type="button"
                    className="flex-1"
                    loading={saving}
                    onClick={handleSaveEdit}
                  >
                    Save
                  </Button1>
                </div>
              </div>
            ) : (
              <>
                {!loading && officeKeyData.ownKey ? (
                  <p className="text-xs text-[#6D7C94] px-1">
                    This agent is using their own office keys.
                  </p>
                ) : null}

                {loading ? (
                  <OfficeIdSupplierListShimmer />
                ) : (
                  <div className="rounded-2xl border border-[#E7EDF5] bg-white overflow-hidden">
                    {OFFICE_SUPPLIERS.map((supplier, index) => {
                      const supplierData = suppliers?.[supplier.id] || {};
                      const isActive = Boolean(supplierData?.isActive);
                      const isLast = index === OFFICE_SUPPLIERS.length - 1;
                      const isToggling = togglingId === supplier.id;

                      return (
                        <div
                          key={supplier.id}
                          className={`flex items-center gap-4 px-4 py-4 ${
                            !isLast ? "border-b border-[#EEF2F7]" : ""
                          }`}
                        >
                          <div className="flex flex-1 items-center gap-3 min-w-0">
                            <span
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${supplier.badgeClass}`}
                            >
                              {supplier.badge}
                            </span>
                            <span className="text-sm font-medium text-primary truncate">
                              {supplier.name}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <Switch
                              checked={isActive}
                              color="primary"
                              disabled={isToggling}
                              onChange={(e) =>
                                handleToggle(supplier, e.target.checked)
                              }
                            />

                            <button
                              type="button"
                              aria-label={`Edit ${supplier.name}`}
                              disabled={!EDITABLE_SUPPLIER_IDS.has(supplier.id)}
                              className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F3F4F6] text-[#6D7C94] hover:bg-[#E5E7EB] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#F3F4F6]"
                              onClick={() => {
                                if (!EDITABLE_SUPPLIER_IDS.has(supplier.id))
                                  return;
                                setEditSupplier(supplier);
                                setEditForm(supplierData);
                                setAmadeusSoapFiles([]);
                              }}
                            >
                              <IconComponent
                                icon="tabler:edit"
                                className="!w-5 !h-5"
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}
